import { CustomIcon } from "components/icon/customIcon.component";
import { CustomLink, Text } from "components/Typography/text.styled";
import { Color } from "config/colors";
import { FontFamily } from "config/font";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRouteMatch } from "react-router-dom";
import { useAuthStore } from "stores/authStore/authStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useMenuList } from "../menu-utils";
import { DrawerCompanyList } from "../menuList/drawerCompanyList";
import { DrawerMenuListStyled } from "../menuList/drawerMenuList.styled";
import { MenuItems } from "../menuList/menuItems.component";
import {
  CompaniesLi,
  DrawerSelect,
  NavIconLi,
  NavLi,
} from "../menuList/navBarMenuList.styled";
import { CloseIcon, DrawerStyled, LinkLabel } from "./drawer.styled";
import { useSignOut } from "../hooks/useSignOut";

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Mobile drawer navigation component.
 */
export const Drawer: React.FC<Props> = (props): React.ReactElement => {
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const [authState] = useAuthStore();
  const [companyState] = useCompanyStore();
  const [isCompanySelectOpen, setIsCompanySelectOpen] = useState(false);
  const menuList = useMenuList(authState.accountType, t);
  const signOut = useSignOut();

  const handleSignOut = async (): Promise<void> => {
    await signOut(() => props.setOpen(false));
  };

  const isProfileActive = window.location.pathname === "/company/profile";
  const isSettingsActive = window.location.pathname === "/company/settings";
  const isRegistrationActive =
    window.location.pathname === "/company/registration";

  return (
    <DrawerStyled open={props.open} className="drawer">
      <CloseIcon
        name="cross"
        size="40px"
        onClick={() => {
          props.setOpen(!props.open);
        }}
      />

      <DrawerMenuListStyled>
        {authState.signedIn && companyState.company && (
          <>
            <MenuItems
              menuList={menuList}
              open={props.open}
              setOpen={props.setOpen}
            />

            <NavIconLi active={isProfileActive}>
              <CustomLink
                color={Color.SeaBlue500}
                to="/company/profile"
                onClick={() => {
                  props.setOpen(false);
                }}
              >
                <CustomIcon
                  name="person"
                  color={Color.SeaBlue500}
                  padding="5px"
                />
                <LinkLabel>{t("ProfileCompany.Profile")}</LinkLabel>
              </CustomLink>
            </NavIconLi>
            <NavIconLi active={isSettingsActive}>
              <CustomLink
                color={Color.SeaBlue500}
                to="/company/settings"
                onClick={() => {
                  props.setOpen(false);
                }}
              >
                <CustomIcon
                  name="settings"
                  color={Color.SeaBlue500}
                  padding="5px"
                />
                <LinkLabel>{t("General.Settings")}</LinkLabel>
              </CustomLink>
            </NavIconLi>
            <CompaniesLi>
              <DrawerSelect
                color={Color.SeaBlue500}
                onClick={() => {
                  setIsCompanySelectOpen(!isCompanySelectOpen);
                }}
              >
                <Text
                  fontFamily={FontFamily.MontserratSemiBold}
                  color={Color.SeaBlue500}
                  style={{ margin: 0, textAlign: "center" }}
                >
                  {t("CompanySelect.YourCompanies")}
                </Text>

                <CustomIcon
                  name={isCompanySelectOpen ? "chevron-up" : "chevron-down"}
                  color={Color.SeaBlue500}
                  padding="5px"
                />
              </DrawerSelect>
            </CompaniesLi>

            {isCompanySelectOpen && (
              <DrawerCompanyList
                setCompanySelectOpen={setIsCompanySelectOpen}
                setOpen={props.setOpen}
              />
            )}

            <NavIconLi active={isRegistrationActive}>
              <CustomLink
                color={Color.SeaBlue500}
                to="/company/registration"
                onClick={() => {
                  props.setOpen(false);
                }}
              >
                <CustomIcon
                  name="plus"
                  color={Color.SeaBlue500}
                  padding="5px"
                />
                <LinkLabel>{t("CompanySelect.AddCompany")}</LinkLabel>
              </CustomLink>
            </NavIconLi>
            <NavIconLi active={false}>
              <CustomLink color={Color.BurntSienna} onClick={handleSignOut}>
                <CustomIcon name="logout" padding="5px" />
                <Text
                  fontFamily={FontFamily.MontserratSemiBold}
                  style={{ margin: 0 }}
                >
                  {t("ProfileCompany.SignOut")}
                </Text>
              </CustomLink>
            </NavIconLi>
          </>
        )}
        {authState.signedIn && !companyState.company && (
          <NavLi
            active={false}
            color={Color.BurntSienna}
            onClick={handleSignOut}
          >
            <CustomLink to={`${path}/login`} color={Color.Destructive}>
              {t("ProfileCompany.SignOut")}
            </CustomLink>
          </NavLi>
        )}
        {!authState.signedIn && (
          <NavLi active={false}>
            <CustomLink to={`${path}/login`} color={Color.Destructive}>
              {t("LoginCompany.Login")}
            </CustomLink>
          </NavLi>
        )}
      </DrawerMenuListStyled>
    </DrawerStyled>
  );
};
